<template>
    <div>
        <card title="PBM Record">
            <b-row id="search-row">
                <b-col>
                    <SearchComponent :id="PBMRecord && +PBMRecord.id" v-model="PBMRecord" label="PBM"
                        search-u-r-l="/PharmacyBenefitManager" create-new-object create-new-label="Create PBM"
                        prepopulate :disabled="!disableFields" @input="onPBMChange" />
                </b-col>
            </b-row>
            <b-row id="details-row">
                <b-col cols="6">
                    <fg-input v-model="PBMRecord.name" label="PBM" max-length="30" required :disabled="disableFields" />
                </b-col>
                <b-col cols="6">
                    <EnumSelector v-model="PBMFlag" :disabled="disableFields" :enum-codes="PBMflags" label="Flag"
                        required />
                </b-col>
                <b-col cols="6">
                    <fg-input v-model="PBMRecord.vendorId" label="VendorId" max-length="50" :disabled="disableFields" />
                </b-col>
                <b-col cols="6">
                    <fg-input label="Minimum DS Mail Order NPI" type="number"
                        v-model.number="PBMRecord.mailOrderDaySupply" :disabled="disableFields" />
                </b-col>
            </b-row>
            <b-row id="price-schedule-row">
                <b-col cols="6">
                    <SearchComponent :id="PBMRecord.defaultPriceSchedule && +PBMRecord.defaultPriceSchedule.id"
                        v-model="PBMRecord.defaultPriceSchedule"
                        label="Default Price Schedule"
                        search-u-r-l="/PriceSchedule"
                        :disabled="disableFields"
                        prepopulate clearable
                        @input="onPriceScheduleChange" />
                </b-col>
                <b-col cols="6">
                    <div v-if="PBMRecord.defaultPriceSchedule" class="form-group">
                        <label>Price Schedule Details:</label>
                        <div class="text-muted small">
                            <div>Basis: {{ getBasisName(PBMRecord.defaultPriceSchedule.basis) }}</div>
                            <div>Factor: {{ PBMRecord.defaultPriceSchedule.factor }}</div>
                            <div v-if="PBMRecord.defaultPriceSchedule.flatMarkup">Flat Markup: ${{ PBMRecord.defaultPriceSchedule.markup }}</div>
                            <div v-else>Markup: {{ (PBMRecord.defaultPriceSchedule.markup * 100).toFixed(2) }}%</div>
                        </div>
                    </div>
                </b-col>
            </b-row>
            <b-row id="allowed-states-row">
                <b-col cols="6">
                    <search-component :id="allowedStore && +allowedStore.id" v-model="allowedStore" label="Store"
                        search-u-r-l="/Store" prepopulate :disabled="disableFields" />
                </b-col>
                <b-col>
                    <label>
                        Allowed Retail States{{ allowedStore.name?.length > 0 ? ' for ' + allowedStore.name : '' }}:
                    </label>
                    <multiselect v-model="allowedStatesForStore" :options="stateOptions" group-label="label"
                        group-values="states" multiple group-select :close-on-select="false" :clear-on-select="false"
                        :disabled="disableFields || !allowedStore.id">
                        <template #selection="{ values, search, isOpen }">
                            <span class="multiselect__single" v-if="values.length" v-show="!isOpen">
                                {{ values.length }} states selected
                            </span>
                        </template>
                    </multiselect>
                </b-col>
            </b-row>
            <b-row id="lists-row">
                <b-col id="bins-col">
                    <b-row>
                        <b-col>
                            <fg-input @keyup.enter="binEntered" max-length="6" label="BINs" :disabled="disableFields" />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col v-for="bin in sortedBins" :key="bin" cols="2">
                            {{ bin }}
                            <b-button-close @click="removeBin(bin)" text-variant="danger" />
                        </b-col>
                    </b-row>
                </b-col>
                <b-col id="exempt-ndc-col">
                    <b-row>
                        <b-col>
                            <fg-input @keyup.enter="ndcEntered" max-length="13" label="Exempt Drugs (NDC11)"
                                :disabled="disableFields" />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col v-for="ndc in PBMRecord.exemptedNdcs" :key="ndc" cols="3">
                            {{ ndc }}
                            <b-button-close @click="removeNdc(ndc)" text-variant="danger" />
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <NewClearSaveButtons ref="newClearSaveButtons"
                :titles="_buttonTitles"
                :disable="disableFields" :disable-clear="disableFields" :disable-save="!isFormValid"
                disable-save-after-click
                @cancel-clicked="backToPatientDashboard" @clear-clicked="showConfirmationModal" @new-clicked="createPBM"
                @save-clicked="submitPBM" />
        </card>
    </div>
</template>
<script lang="ts">
import { Flags, PharmacyBenefitManager } from '@/models/PharmacyBenefitManager';
import { PriceSchedule, BasisEnum } from '@/models/PriceSchedule';
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import NewClearSaveButtons, { ButtonTitles } from '@/components/NewClearSaveButtons.vue';
import axios from 'axios';
import EnumSelector from '@/components/EnumSelector.vue';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import SearchComponent from "@/components/SearchComponent.vue";
import Multiselect from "vue-multiselect";
import { ValidValues } from '@/data/ValidValues';
import NotificationTypes = NotificationOptions.NotificationTypes;
import { Store } from "@/models/Store";
import { ButtonTitles } from "@/components/ButtonTitles";

@Component({
    name: "PBMComponent",
    components: {
        Multiselect,
        SearchComponent,
        NewClearSaveButtons,
        EnumSelector,
    },
})
export default class PBMComponent extends Vue {
    private allowedStore: Store = new Store();
    private PBMRecord: PharmacyBenefitManager = new PharmacyBenefitManager();
    @Ref() newClearSaveButtons!: NewClearSaveButtons;

    protected PBMflags = Flags;
    private PBMFlag: number = 0;
    @Prop({ type: Boolean, default: false }) private createNewPbm!: boolean;
    @Prop({ type: Boolean, default: false }) private noCancelButton!: boolean;
    @Prop({ type: Number, default: null }) private pbmId!: number | null;
    private stateOptions = [{ label: "All States", states: ValidValues.addressStates }];

    get allowedStatesForStore(): string[] {
        const idx = this.PBMRecord.allowedStates.findIndex(value => value.storeId == this.allowedStore.id);
        if (idx === -1) return [];
        return this.PBMRecord.allowedStates[idx].states;
    }

    // noinspection JSUnusedGlobalSymbols
    set allowedStatesForStore(value: string[]) {
        const idx = this.PBMRecord.allowedStates.findIndex(value => value.storeId == this.allowedStore.id);
        if (idx === -1) {
            this.PBMRecord.allowedStates.push({ storeId: +this.allowedStore.id, states: value });
        } else {
            this.PBMRecord.allowedStates[idx].states = value;
        }
    }

    created() {
        if (this.createNewPbm) {
            this.createPBM();
        } else if (this.pbmId) {
            // Load the PBM if an ID was provided
            this.loadPBM(this.pbmId);
        }
    }

    @Watch('pbmId')
    onPBMIdChanged(newVal: number | null, oldVal: number | null) {
        if (newVal && newVal != oldVal) {
            this.loadPBM(newVal);
        }
    }

    async loadPBM(id: number) {
        try {
            const response = await axios.get(`/PharmacyBenefitManager/${id}`);
            this.PBMRecord = response.data;
            this.PBMFlag = this.PBMRecord.flags;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.error("Error loading PBM:", error);
        }
    }

    clearForm() {
        this.PBMRecord = new PharmacyBenefitManager();
        this.allowedStore = new Store();
        this.PBMFlag = 0;
        this.$router.push({ name: 'PharmacyBenefitManager' });
    }

    createPBM() {
        this.PBMRecord = new PharmacyBenefitManager();
        this.PBMRecord.id = 0;
    }

    backToPatientDashboard() {
        if (this.createNewPbm) {
            this.$emit("close");
            return;
        }
    }

    showConfirmationModal() {
        this.$bvModal.msgBoxConfirm('Are you sure you want to clear?', {
            title: 'Confirm',
            okVariant: 'danger',
            centered: true,
        })
            .then(val => {
                if (val) this.clearForm();
            })
            .catch(err => {
                console.log("Error caught in showConfirmationModal()");
                console.error(err);
            });
    }

    submitPBM() {
        if (this.PBMRecord?.name != undefined || this.PBMRecord?.name != "" || this.PBMRecord?.name != null) {
            this.PBMRecord.flags = this.PBMFlag;
            axios.post<PharmacyBenefitManager>(`/PharmacyBenefitManager`, this.PBMRecord)
                .then(res => {
                    this.$notification(NotificationOptions.notificationOptionsPreset("Pharmacy Benefit Manager Created Successfully", NotificationOptions.NotificationTypes.success));
                    this.PBMRecord = res.data;
                    (this.newClearSaveButtons as any).resetSaveState();
                    this.$emit('created', this.PBMRecord);
                })
                .catch(err => {
                    this.$notification(NotificationOptions.error(err));
                    console.error("Error while saving PBM.", { err, response: err.response });
                });
        }

    }

    ndcEntered(val: any) {
        let tarVal = val.target.value;
        let idx = this.PBMRecord.exemptedNdcs.indexOf(tarVal);
        if (idx != -1) {
            this.$notification(NotificationOptions.notificationOptionsPreset("NDC already present", NotificationTypes.danger));
            return;
        }
        if (tarVal.length == 13) {
            tarVal = tarVal.replace('-', '');
        }
        if (tarVal.length == 11) {
            this.PBMRecord.exemptedNdcs.push(tarVal);
        } else {
            this.$notification(NotificationOptions.notificationOptionsPreset("Invalid NDC11 length", NotificationTypes.danger));
        }
    }

    binEntered(val: any) {
        const tarVal = val.target.value;
        let idx = this.PBMRecord.bins.indexOf(tarVal);

        if (idx != -1) {
            this.$notification(NotificationOptions.notificationOptionsPreset("BIN already present", NotificationTypes.danger));
            return;
        }

        if (tarVal.length == 6) {
            this.PBMRecord.bins.push(tarVal);
        } else {
            this.$notification(NotificationOptions.notificationOptionsPreset("Invalid BIN length", NotificationOptions.NotificationTypes.warning));
        }
    }

    removeNdc(val: any) {
        let idx = this.PBMRecord.exemptedNdcs.indexOf(val);
        if (idx != -1) {
            this.PBMRecord.exemptedNdcs.splice(idx, 1);
        }
    }

    removeBin(val: any) {
        let idx = this.PBMRecord.bins.indexOf(val);
        if (idx != -1) {
            this.PBMRecord.bins.splice(idx, 1);
        }
    }

    async onPBMChange(value: PharmacyBenefitManager) {
        try {
            this.PBMRecord = value;
            if (value.id) {
                let pbmResp = await axios.get<PharmacyBenefitManager>(`/api/PharmacyBenefitManager/${value.id}`);
                this.PBMRecord = new PharmacyBenefitManager(pbmResp.data);
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get sortedBins(): string[] {
        return this.PBMRecord?.bins?.sort();
    }

    get disableFields(): boolean {
        return isNaN(this.PBMRecord.id);
    }

    get isFormValid() {
        return true;
    }

    onPriceScheduleChange(value: PriceSchedule) {
        this.PBMRecord.defaultPriceSchedule = value;
        this.PBMRecord.defaultPriceScheduleId = value?.id || null;
    }

    getBasisName(basis: BasisEnum): string {
        switch (basis) {
            case BasisEnum.AWP:
                return 'AWP (Average Wholesale Price)';
            case BasisEnum.Cost:
                return 'Cost';
            default:
                return 'Unknown';
        }
    }

    private get _buttonTitles() {
        const res: ButtonTitles = {
            save: "Save Changes",
            clear: "Clear",
            new: "Create New PBM",
            cancel: this.noCancelButton ? "" : "Back to Audit Record",
        };
        return res;
    }
}
</script>
